import {
    Box,
    Button,
    Chip,
    Divider,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Tiny } from 'components/Typography';
import TimeRangeWorkshop from 'utils/TimeRangeWorkshop';
import EventIcon from '@mui/icons-material/Event';
import FormatDate from 'utils/formatDate';
import formatCurrency from 'utils/formatCurrency';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

const WorkshopCard = ({ item, selected, isPaid = false, isSold = false, chooseWorkshop }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 3,
                height: '100%',
                maxWidth: 360,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                border: `1px solid ${theme.palette.divider}`,
                transition:
                    'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                backgroundColor: theme.palette.background.paper,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                    borderColor: theme.palette.primary.main,
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Chip
                    label={item?.workshopType?.label}
                    color={item?.workshopType?.chipColor}
                    variant="filled"
                    sx={{ fontWeight: 700, borderRadius: 2, color: 'white' }}
                />
                {selected && (
                    <CheckCircleTwoToneIcon fontSize="large" color="primary" />
                )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {item.name}
            </Typography>

            {item?.description && (
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    {item.description}
                </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AccessTimeIcon fontSize="small" color="primary" />
                    <Tiny color="text.secondary" fontSize={13}>
                        <strong>Time:</strong>{' '}
                        {item?.mapping_ws_to_time_slot?.length
                            ? `${TimeRangeWorkshop(
                                  item.mapping_ws_to_time_slot,
                              )}${(() => {
                                  const slots = item.mapping_ws_to_time_slot;
                                  const start = new Date(
                                      slots[0].ws_time_slot.start_time,
                                  );
                                  const end = new Date(
                                      slots[
                                          slots.length - 1
                                      ].ws_time_slot.end_time,
                                  );
                                  const hours =
                                      Math.round(
                                          ((end - start) / 3600000) * 10,
                                      ) / 10;
                                  return ` (${hours} Hours)`;
                              })()}`
                            : item?.time || '-'}
                    </Tiny>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <EventIcon fontSize="small" color="primary" />
                    <Tiny color="text.secondary" fontSize={13}>
                        <strong>Date:</strong>{' '}
                        {item?.mapping_ws_to_time_slot?.length
                            ? (() => {
                                  const slots = item.mapping_ws_to_time_slot;
                                  const start =
                                      slots[0].ws_time_slot.start_time;
                                  return FormatDate(start, 'MMMM dd, yyyy');
                              })()
                            : item?.date || '-'}
                    </Tiny>
                </Stack>
            </Stack>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 'auto',
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    {formatCurrency(
                        parseInt(
                            item?.workshop_type?.price ?? item?.price ?? 0,
                        ),
                    )}
                </Typography>
                <Button
                    variant="contained"
                    color={selected || isPaid ? 'secondary' : 'primary'}
                    onClick={() => chooseWorkshop(item)}
                    size="small"
                    disabled={isPaid || isSold}
                    sx={{
                        borderRadius: 2,
                        px: 2.5,
                        textTransform: 'uppercase',
                    }}
                >
                    {isSold ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            Sold
                        </Box>
                    ) : isPaid ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <CheckCircleTwoToneIcon fontSize="small" />
                            Paid
                        </Box>
                    ) : selected ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <CancelOutlinedIcon fontSize="small" />
                            Remove
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <AddCircleOutlineOutlinedIcon fontSize="small" />
                            Select
                        </Box>
                    )}
                </Button>
            </Box>
        </Box>
    );
};

export default WorkshopCard;
